import { store } from '@/app/store';
import { authConfig } from '@/services/auth/auth-config';
import { sentryService } from '@/services/telemetry/sentry';
import type {
  GenerateGroupPortraitInput,
  GeneratePortraitInput,
  GenerationJobStatus,
  GenerationSubmitResponse,
  GroupPortraitSubmitResponse,
  ReserveConfigsItem,
  ReserveConfigsOptions,
  ReserveConfigsResponse,
  UpscalePortraitsResponse,
} from '@/shared/types/api';
import {
  api,
  callBreadcrumbs,
  instrument,
  requireSessionId,
  unwrap,
} from '../client';
import { sendKeepalive } from '../http';

// Answers 202 at once; generation failures surface in the poll body, not here.
export function submitGeneration(
  input: GeneratePortraitInput
): Promise<GenerationSubmitResponse> {
  const {
    faceUuid,
    styleId,
    age_group,
    gender,
    race,
    priority,
    configUuid,
    requestId,
  } = input;

  return instrument<GenerationSubmitResponse>(
    {
      span: 'api.submit_generation',
      errorTag: 'generations_submit_generation',
      // THE-300/301: demographic values never go to telemetry.
      tags: { 'api.style_id': styleId },
      before: {
        message: 'Submitting generation job',
        data: { style_id: styleId, request_id: requestId },
      },
      after: (r) => ({
        message: 'Generation job accepted',
        data: { style_id: styleId, job_id: r?.job_id },
      }),
      errorExtra: { style_id: styleId, request_id: requestId },
    },
    () => {
      const sessionId = requireSessionId();
      return unwrap(
        api.POST('/v1/generations', {
          body: {
            session_id: sessionId,
            uploaded_photo_uuid: faceUuid,
            bucket_name: store.kiosk?.portrait_s3_bucket_name,
            style_id: styleId,
            config_uuid: configUuid || undefined,
            priority: priority ?? 'medium',
            age_group,
            gender,
            race,
            request_id: requestId,
          },
        })
      );
    }
  );
}

// Group portraits: the kiosk sends the photo uuids that it already uploaded,
// and the backend selects the story from its own settings. Each person goes
// with the gender, the age group and the race of that person, because the
// prompt describes each person. The caller gives the people left to right,
// and the two lists keep that order. The result is an ordinary
// generated_images row, thus getGenerationStatus below polls it too.
//
// The submit is the live availability check. A group that no story takes
// (404) and a person with a missing trait (422) are expected answers: the
// kiosk shows the singles only, and Sentry gets a breadcrumb, not an event.
export const GROUP_PORTRAIT_NOT_OFFERED_CODES = [
  'V1_GROUP_PORTRAIT_NOT_CONFIGURED',
  'V1_GROUP_PORTRAIT_SUBJECT_INVALID',
];

export function submitGroupPortrait(
  input: GenerateGroupPortraitInput
): Promise<GroupPortraitSubmitResponse> {
  const { faces, style_id, bucket_name, request_id } = input;
  const faceCount = faces?.length ?? 0;

  return instrument<GroupPortraitSubmitResponse>(
    {
      span: 'api.submit_group_portrait',
      errorTag: 'group_portraits_submit_group_portrait',
      tags: { 'api.style_id': style_id, 'api.face_count': faceCount },
      before: {
        message: 'Submitting group portrait job',
        data: { style_id, face_count: faceCount, request_id },
      },
      after: (r) => ({
        message: 'Group portrait job accepted',
        data: {
          style_id,
          face_count: faceCount,
          job_id: r?.job_id,
          workflow_id: r?.workflow_id,
        },
      }),
      errorExtra: { style_id, face_count: faceCount, request_id },
      skipSentryOnCodes: GROUP_PORTRAIT_NOT_OFFERED_CODES,
    },
    () => {
      const sessionId = requireSessionId();
      return unwrap(
        api.POST('/v1/group-portraits', {
          body: {
            session_id: sessionId,
            uploaded_photo_uuids: faces.map((face) => face.uuid),
            subjects: faces.map(({ gender, age_group, race }) => ({
              gender,
              age_group,
              race,
            })),
            style_id,
            bucket_name,
            request_id,
          },
        })
      );
    }
  );
}

// Deliberately un-instrumented and silent: this fires every 1-3s while a
// portrait generates; the poll loop in PortraitGeneration.js owns
// breadcrumbs, metrics, and error interpretation (404 = never accepted,
// anything else = transient).
export function getGenerationStatus(
  jobId: string
): Promise<GenerationJobStatus> {
  return unwrap(
    api.GET('/v1/generations/{image_id}', {
      params: { path: { image_id: jobId } },
      silent: true,
    })
  );
}

const CANCEL_GENERATIONS_URL = '/api/v1/generations/cancellations';

// The cancel is session-scoped, because a submit still in flight has no job id
// here while its row already exists on the server. The kiosk sends it only when
// the session ends or the page closes. A cancel while the session goes on fails
// the portrait that the visitor still waits for.
export function sendGenerationCancel(sessionId: string): void {
  const apiKey = authConfig.getApiKey();
  if (!apiKey) {
    return;
  }
  sendKeepalive(
    CANCEL_GENERATIONS_URL,
    { session_id: sessionId },
    { apiKey, sessionId }
  ).then((response) => {
    // A backend from before the endpoint answers 404.
    if (response === null || response.ok || response.status === 404) {
      return;
    }
    sentryService.addBreadcrumb(
      'generation',
      `Generation cancel refused: HTTP ${response.status}`,
      { status: response.status },
      'warning'
    );
  });
}

export function reserveConfigs(
  items: ReserveConfigsItem[],
  options: ReserveConfigsOptions = {}
): Promise<ReserveConfigsResponse> {
  return instrument<ReserveConfigsResponse>(
    {
      span: 'api.get_n_configs',
      errorTag: 'generations_reserve_configs',
      tags: { 'api.items_count': items.length },
      errorExtra: { items_count: items.length },
    },
    () => {
      const sessionId = requireSessionId();
      return unwrap(
        api.POST('/v1/generations/config-reservations', {
          body: {
            session_id: sessionId,
            items: items.map((item) => ({
              style_id: item.styleId,
              age_group: item.age_group,
              gender: item.gender,
              race: item.race,
              motif: item.motif || undefined,
            })),
            repeat_with_seed: options.repeatWithSeed ?? false,
          },
        })
      );
    }
  );
}

export function upscaleB2CPortraits(
  imageUuids: string[]
): Promise<UpscalePortraitsResponse> {
  const imageCount = imageUuids?.length ?? 0;
  return instrument<UpscalePortraitsResponse>(
    {
      span: 'api.upscale_b2c_portraits',
      errorTag: 'sessions_upscale_portraits',
      tags: { 'api.image_count': imageCount },
      ...callBreadcrumbs(
        'Upscaling B2C portraits',
        'B2C portraits upscale initiated',
        { image_count: imageCount }
      ),
      trackErrorCode: 'api_error_upscaling',
    },
    () => {
      const sessionId = requireSessionId();
      return unwrap(
        api.POST('/v1/sessions/{session_id}/upscale-portraits', {
          params: { path: { session_id: sessionId } },
          body: {
            bucket_name: store.kiosk?.portrait_s3_bucket_name as string,
            picked_image_uuids: imageUuids,
          },
        })
      );
    }
  );
}
